// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import AppStoreLinks from './AppStoreLinks';
import { useAppVersion } from '../hooks/useAppVersion';

type Props = {
  children: ReactNode;
};

export default function MarketingLayout({ children }: Props) {
  const { t } = useTranslation();
  const appVersion = useAppVersion();
  const { pathname } = useLocation();

  const navLinks = [
    { path: '/', label: t('marketing.nav.home') },
    { path: '/pricing', label: t('marketing.nav.pricing') },
    { path: '/why-clubview', label: t('marketing.nav.why') },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center">
            <Logo variant="full" size="md" />
          </Link>
          <nav className="hidden items-center gap-6 md:flex" aria-label="Marketing navigation">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition ${
                  pathname === link.path
                    ? 'text-brand-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              {t('marketing.nav.login')}
            </Link>
            <Link
              to="/register-club"
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700"
            >
              {t('marketing.nav.getStarted')}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between">
            <Logo variant="full" size="sm" />
            <div className="flex gap-6 text-sm text-slate-500">
              <Link to="/terms" className="hover:text-brand-600">{t('common.terms')}</Link>
              <Link to="/privacy" className="hover:text-brand-600">{t('common.privacy')}</Link>
              <Link to="/cookies" className="hover:text-brand-600">{t('common.cookies')}</Link>
              <Link to="/directory" className="hover:text-brand-600">{t('marketing.nav.directory')}</Link>
            </div>
          </div>
          <div className="mt-4 flex justify-center md:justify-start">
            <AppStoreLinks />
          </div>
          <p className="mt-4 text-center text-xs text-slate-500 md:text-left">
            {t('common.copyright', { year: new Date().getFullYear() })}{appVersion && ` · ${appVersion}`}
          </p>
        </div>
      </footer>
    </div>
  );
}
